/**
 * EmptyState — reusable "no data yet" panel with an inline SVG illustration.
 *
 * Five built-in illustrations (no external file deps) keyed by `kind`:
 *   inbox     → empty alerts, empty signal log
 *   chart     → no chart data / no history yet
 *   search    → search returned no results
 *   trade     → no trades / no open positions
 *   error     → error fetching
 *
 * Pass `action` for a CTA button beneath the body text.
 */
import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useAppTheme } from '../../hooks/useAppTheme';

export type EmptyStateKind = 'inbox' | 'chart' | 'search' | 'trade' | 'error';

interface Props {
  kind?: EmptyStateKind;
  title: string;
  description?: React.ReactNode;
  action?: { label: string; onClick: () => void; color?: 'primary' | 'inherit' | 'success' | 'error' };
  size?: 'sm' | 'md';
}

const EmptyState: React.FC<Props> = ({ kind = 'inbox', title, description, action, size = 'md' }) => {
  const { C, isDark } = useAppTheme();
  const stroke = isDark ? '#7f8794' : '#9aa3ad';
  const fill = isDark ? '#1c2128' : '#f6f8fa';
  const accent = kind === 'error' ? C.bear : kind === 'trade' ? C.bull : kind === 'search' ? C.warn : C.neutral;

  const dim = size === 'sm' ? 80 : 112;

  const svg = (() => {
    const props = { width: dim, height: dim, viewBox: '0 0 120 120', fill: 'none' as const };
    switch (kind) {
      case 'chart':
        return (
          <svg {...props}>
            <rect x="10" y="20" width="100" height="80" rx="6" fill={fill} stroke={stroke} strokeWidth="1.5" />
            <path d="M20 80 L40 60 L55 70 L75 45 L100 55" stroke={accent} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="40" cy="60" r="2.5" fill={accent} />
            <circle cx="75" cy="45" r="2.5" fill={accent} />
            <line x1="20" y1="95" x2="100" y2="95" stroke={stroke} strokeWidth="1" strokeDasharray="2 3" opacity="0.5" />
          </svg>
        );
      case 'search':
        return (
          <svg {...props}>
            <circle cx="48" cy="48" r="28" fill={fill} stroke={stroke} strokeWidth="2" />
            <line x1="70" y1="70" x2="95" y2="95" stroke={stroke} strokeWidth="3" strokeLinecap="round" />
            <line x1="40" y1="48" x2="56" y2="48" stroke={accent} strokeWidth="2" strokeLinecap="round" />
            <line x1="48" y1="40" x2="48" y2="56" stroke={accent} strokeWidth="2" strokeLinecap="round" />
          </svg>
        );
      case 'trade':
        return (
          <svg {...props}>
            <rect x="20" y="40" width="80" height="60" rx="4" fill={fill} stroke={stroke} strokeWidth="1.5" />
            <line x1="30" y1="55" x2="90" y2="55" stroke={stroke} strokeWidth="1" opacity="0.5" />
            <line x1="30" y1="70" x2="90" y2="70" stroke={stroke} strokeWidth="1" opacity="0.5" />
            <line x1="30" y1="85" x2="90" y2="85" stroke={stroke} strokeWidth="1" opacity="0.5" />
            <path d="M60 15 L60 35 M52 22 L60 14 L68 22" stroke={accent} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        );
      case 'error':
        return (
          <svg {...props}>
            <circle cx="60" cy="60" r="40" fill={fill} stroke={C.bear} strokeWidth="2" />
            <line x1="45" y1="45" x2="75" y2="75" stroke={C.bear} strokeWidth="3" strokeLinecap="round" />
            <line x1="75" y1="45" x2="45" y2="75" stroke={C.bear} strokeWidth="3" strokeLinecap="round" />
          </svg>
        );
      case 'inbox':
      default:
        return (
          <svg {...props}>
            <path d="M20 50 L20 90 Q20 95 25 95 L95 95 Q100 95 100 90 L100 50 L80 50 L75 65 L45 65 L40 50 Z"
              fill={fill} stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" />
            <path d="M30 50 L40 25 Q42 22 45 22 L75 22 Q78 22 80 25 L90 50" fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" />
            <line x1="48" y1="32" x2="72" y2="32" stroke={accent} strokeWidth="1.5" strokeLinecap="round" />
            <line x1="48" y1="40" x2="72" y2="40" stroke={accent} strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        );
    }
  })();

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      textAlign: 'center', py: size === 'sm' ? 2 : 4, px: 2, gap: 1,
    }}>
      {svg}
      <Typography variant={size === 'sm' ? 'subtitle2' : 'h6'} sx={{ fontWeight: 700, mt: 0.5 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
          {description}
        </Typography>
      )}
      {action && (
        <Button variant="contained" color={action.color || 'primary'} onClick={action.onClick} size="small" sx={{ mt: 1 }}>
          {action.label}
        </Button>
      )}
    </Box>
  );
};

export default EmptyState;
